import React, { useEffect, useState } from "react";
import { Page, setPage } from "features/appstate/AppStateSlice";
import {
  Surveys,
  SurveysDeleteForm,
  Paginated,
  PaginationParams,
  ClientPlanDetails,
  MeoSiteCountList,
  SurveyVocSettingForm,
} from "api/sms-api";
import {
  Spin,
  Table,
  Button,
  Row,
  Popconfirm,
  Alert,
  Menu,
  Switch,
  Dropdown,
  Modal,
  Tooltip,
} from "antd";
import { RootState } from "store/store";
import { connect } from "react-redux";
import {
  getSurveyList,
  deleteSurveys,
  duplicateSurvey,
  changeSurveyVocSetting,
  changeSurveyPublish,
  getMeoSiteCountList,
  deleteAccessData,
} from "./SurveyActions";
import { getClientPlanDetails } from "../contract_details/ContractDetailsActions";
import {
  setSelectedSurveys,
  clearSurvey,
  clearPickedQuestions,
  clearSelectedSurveys,
  clearItems,
} from "./SurveySlice";
import i18n from "i18n";
import { RouteComponentProps, useLocation, useHistory } from "react-router-dom";
import SMSLink from "components/SMSLink";
import { compact } from "lodash";
import {
  PlusOutlined,
  SettingOutlined,
  ShopOutlined,
  CommentOutlined,
  DeleteOutlined,
  CheckOutlined,
  FieldTimeOutlined,
  CopyOutlined,
  DatabaseOutlined,
} from "@ant-design/icons";
import _ from "lodash";
import SimplePagination, {
  PaginatedComponentProps,
} from "components/SimplePagination";
import qs from "qs";
import {
  MODE_SURVEY,
  surveyTypeStore,
  SURVEY_PAGE_BASE,
} from "../../constants";
import SurveyQR from "components/SurveyQR";
import CopyToClipboard from "react-copy-to-clipboard";
import { showNotification } from "utils/notifications";
import { setReviewLinkWarning } from "utils/setReviewLinkWarning";
import VocSettingModal from "./VocSettingModal";
import { isEmptyHtmlContent } from "utils/strings";

const PAGE_LIMIT = 100;

interface SurveyListProps {
  items: Paginated<Surveys>;
  listLoading: boolean;
  getSurveyList: (params: PaginationParams) => void;
  clearItems: () => void;

  deleteSurveys: (params: SurveysDeleteForm) => void;
  multipleDeleteFinished: boolean;
  multipleDeleteLoading: boolean;

  selectedSurveys: Array<{ id: string }>;
  setSelectedSurveys: (selected: Array<{ id: string }>) => void;
  clearSelectedSurveys: () => void;

  setPage: (page: Page) => void;
  clearPickedQuestions: () => void;
  clearSurvey: () => void;

  duplicateSurvey: (params: SurveysDeleteForm) => void;

  clientPlanDetails: ClientPlanDetails;
  getClientPlanDetails: () => void;

  changeSurveyVocSetting: (
    surveyId: string,
    vocSetting: SurveyVocSettingForm
  ) => Promise<boolean>;
  changeSurveyPublish: (surveyId: string) => Promise<boolean>;

  meoSiteCountListLoading: boolean;
  meoSiteCountList: MeoSiteCountList[];
  getMeoSiteCountList: (params: PaginationParams) => void;

  // アクセスデータ削除のプロパティ
  deleteAccessData: (params: SurveysDeleteForm) => void;
  deleteAccessDataLoading: boolean;
  deleteAccessDataFinished: boolean;

  error: any;
}

const SurveyTable: React.FC<
  SurveyListProps & RouteComponentProps & PaginatedComponentProps
> = (props) => {
  const history = useHistory();
  const location = useLocation();
  const parsed = qs.parse(location.search, { ignoreQueryPrefix: true });
  const isSurveyTypeStore = parsed.surveyType === surveyTypeStore;
  let surveyLinkItem = "meoSites";
  let pageItem = Page.meoSites;
  if (!isSurveyTypeStore) {
    surveyLinkItem = "products";
    pageItem = Page.products;
  }
  const [meoSiteCountMap, setMeoSiteCountMap] = useState(new Map());
  const [vocMap, setVocMap] = useState<{ [key: string]: boolean }>({});
  const [publishMap, setPublishMap] = useState<{ [key: string]: boolean }>({});

  const [isVocSettingModalVisible, setIsVocSettingModalVisible] = useState(
    false
  );
  const [
    selectedSurveyForVoc,
    setSelectedSurveyForVoc,
  ] = useState<Surveys | null>(null);
  const [vocSettingsCache, setVocSettingsCache] = useState<{
    [surveyId: string]: {
      isVoc: boolean;
      vocType: number;
      vocTopPageNote: string;
      vocCompletedNote: string;
    };
  }>({});
  const [editingVocSettings, setEditingVocSettings] = useState<{
    isVoc: boolean;
    vocType: number;
    vocTopPageNote: string;
    vocCompletedNote: string;
  } | null>(null);

  useEffect(() => {
    props.setPage(Page.survey);
  }, [props.setPage]);

  useEffect(() => {
    props.getClientPlanDetails();
  }, [props.getClientPlanDetails]);

  useEffect(() => {
    props.clearSurvey();
    props.clearPickedQuestions();
    const params = {
      ..._.omit(parsed, ["mode"]),
    };
    props.getSurveyList({ ...params, limit: PAGE_LIMIT });
  }, [props.getSurveyList, location.search]);

  useEffect(() => {
    const params = {
      ..._.omit(parsed, ["mode"]),
    };
    props.getMeoSiteCountList({
      ...params,
      limit: PAGE_LIMIT,
    });
  }, [props.getSurveyList, location.search]);

  useEffect(() => {
    const map = new Map(
      props.meoSiteCountList.map((item) => [item.surveyId, item.meoSiteCount])
    );
    setMeoSiteCountMap(map);
  }, [props.meoSiteCountList]);

  useEffect(() => {
    if (props.multipleDeleteFinished && !props.multipleDeleteLoading) {
      props.clearSelectedSurveys();
      const params = {
        ..._.omit(parsed, ["mode", "before", "after"]),
      };
      props.getSurveyList({ ...params, limit: PAGE_LIMIT });
    }
  }, [
    props.getSurveyList,
    props.multipleDeleteFinished,
    props.multipleDeleteLoading,
    props.meoSiteCountListLoading,
  ]);

  useEffect(() => {
    if (props.deleteAccessDataFinished && !props.deleteAccessDataLoading) {
      props.clearSelectedSurveys();
      const params = {
        ..._.omit(parsed, ["mode", "before", "after"]),
      };
      props.getSurveyList({ ...params, limit: PAGE_LIMIT });
    }
  }, [
    props.getSurveyList,
    props.deleteAccessDataFinished,
    props.deleteAccessDataLoading,
  ]);

  useEffect(() => {
    const vocMap = props.items.items.reduce(
      (acc: { [key: string]: boolean }, survey) => {
        acc[survey.id] = survey.isVoc;
        return acc;
      },
      {}
    );
    setVocMap(vocMap);
  }, [props.items]);

  useEffect(() => {
    const publishMap = props.items.items.reduce(
      (acc: { [key: string]: boolean }, survey) => {
        acc[survey.id] = survey.publish;
        return acc;
      },
      {}
    );
    setPublishMap(publishMap);
  }, [props.items]);

  useEffect(() => {
    return () => {
      props.clearItems();
      props.clearSelectedSurveys();
    };
  }, []);

  if (props.listLoading || props.meoSiteCountListLoading) {
    return <Spin />;
  }

  const handleQR = (id: string, title: string | null) => {
    Modal.info({
      title: null,
      icon: null,
      content: (
        <SurveyQR
          url={`${SURVEY_PAGE_BASE}/select_store?id=${id}`}
          title={`${title}`}
        />
      ),
    });
  };
  const urlQRCodeDropdown = (id: string, title: string | null) => (
    <Menu>
      <Menu.Item>
        <CopyToClipboard
          text={`${SURVEY_PAGE_BASE}/select_store?id=${id}`}
          onCopy={() =>
            showNotification(i18n.t("survey.table.item.copyUrlNotification"))
          }
        >
          <div>{i18n.t("survey.table.item.url")}</div>
        </CopyToClipboard>
      </Menu.Item>
      <Menu.Item>
        <div onClick={() => handleQR(id, title)}>
          {i18n.t("survey.table.item.qr")}
        </div>
      </Menu.Item>
    </Menu>
  );

  // VOC:VOCスイッチ更新関数
  const handleVocSwitchChange = async (surveyId: string) => {
    const survey = props.items.items.find((item) => item.id === surveyId);
    if (survey) {
      if (vocMap[surveyId]) {
        const cachedSettings = vocSettingsCache[surveyId];
        const vocSetting = {
          isVoc: !(cachedSettings?.isVoc ?? survey.isVoc ?? false),
          vocType: cachedSettings?.vocType || survey.vocType || 1,
          vocTopPageNote:
            cachedSettings !== undefined
              ? cachedSettings.vocTopPageNote?.trim() ||
                i18n.t("survey.defaultVocTopPageNote")
              : survey.vocTopPageNote?.trim() ||
                i18n.t("survey.defaultVocTopPageNote"),
          vocCompletedNote:
            cachedSettings !== undefined
              ? cachedSettings.vocCompletedNote?.trim() ||
                i18n.t("survey.defaultVocCompletedNote")
              : survey.vocCompletedNote?.trim() ||
                i18n.t("survey.defaultVocCompletedNote"),
        };

        const result = await props.changeSurveyVocSetting(surveyId, vocSetting);

        if (result) {
          setVocMap((prev) => ({ ...prev, [surveyId]: false }));
        }
      } else {
        // VOCをオンにする場合（モーダル表示）
        setSelectedSurveyForVoc(survey);

        // キャッシュがある場合
        let topPageNoteValue = i18n.t("survey.defaultVocTopPageNote");
        let completedNoteValue = i18n.t("survey.defaultVocCompletedNote");

        if (vocSettingsCache[surveyId] !== undefined) {
          // キャッシュがある場合
          if (!isEmptyHtmlContent(vocSettingsCache[surveyId].vocTopPageNote)) {
            topPageNoteValue = vocSettingsCache[surveyId].vocTopPageNote;
          }
          if (
            !isEmptyHtmlContent(vocSettingsCache[surveyId].vocCompletedNote)
          ) {
            completedNoteValue = vocSettingsCache[surveyId].vocCompletedNote;
          }
        } else {
          // キャッシュがない場合、surveyから取得
          if (!isEmptyHtmlContent(survey.vocTopPageNote)) {
            topPageNoteValue =
              survey.vocTopPageNote || i18n.t("survey.defaultVocTopPageNote");
          }
          if (!isEmptyHtmlContent(survey.vocCompletedNote)) {
            completedNoteValue =
              survey.vocCompletedNote ||
              i18n.t("survey.defaultVocCompletedNote");
          }
        }

        const settingsData = {
          isVoc: true,
          vocType: vocSettingsCache[surveyId]?.vocType ?? survey.vocType ?? 1,
          vocTopPageNote: topPageNoteValue,
          vocCompletedNote: completedNoteValue,
        };

        setEditingVocSettings(settingsData);
        setIsVocSettingModalVisible(true);
      }
    }
  };

  // VOC:VOC設定モーダルを開く関数（ギアアイコンクリック時用）
  const handleOpenVocSettingModal = (surveyId: string) => {
    const survey = props.items.items.find((item) => item.id === surveyId);
    if (survey) {
      setSelectedSurveyForVoc(survey);

      let topPageNoteValue = i18n.t("survey.defaultVocTopPageNote");
      let completedNoteValue = i18n.t("survey.defaultVocCompletedNote");

      if (vocSettingsCache[surveyId] !== undefined) {
        if (!isEmptyHtmlContent(vocSettingsCache[surveyId].vocTopPageNote)) {
          topPageNoteValue = vocSettingsCache[surveyId].vocTopPageNote;
        }
        if (!isEmptyHtmlContent(vocSettingsCache[surveyId].vocCompletedNote)) {
          completedNoteValue = vocSettingsCache[surveyId].vocCompletedNote;
        }
      } else {
        if (!isEmptyHtmlContent(survey.vocTopPageNote)) {
          topPageNoteValue =
            survey.vocTopPageNote || i18n.t("survey.defaultVocTopPageNote");
        }
        if (!isEmptyHtmlContent(survey.vocCompletedNote)) {
          completedNoteValue =
            survey.vocCompletedNote || i18n.t("survey.defaultVocCompletedNote");
        }
      }

      const settingsData = {
        isVoc: true,
        vocType: vocSettingsCache[surveyId]?.vocType ?? survey.vocType ?? 1,
        vocTopPageNote: topPageNoteValue,
        vocCompletedNote: completedNoteValue,
      };

      setEditingVocSettings(settingsData);
      setIsVocSettingModalVisible(true);
    }
  };

  // VOC:VOC設定保存関数
  const handleVocSettingSave = async () => {
    if (selectedSurveyForVoc && editingVocSettings) {
      const vocSetting = {
        isVoc: editingVocSettings.isVoc,
        vocType: editingVocSettings.vocType,
        vocTopPageNote: isEmptyHtmlContent(editingVocSettings.vocTopPageNote)
          ? i18n.t("survey.defaultVocTopPageNote")
          : editingVocSettings.vocTopPageNote,
        vocCompletedNote: isEmptyHtmlContent(
          editingVocSettings.vocCompletedNote
        )
          ? i18n.t("survey.defaultVocCompletedNote")
          : editingVocSettings.vocCompletedNote,
      };

      const result = await props.changeSurveyVocSetting(
        selectedSurveyForVoc.id,
        vocSetting
      );

      if (result) {
        setVocMap((prev) => ({ ...prev, [selectedSurveyForVoc.id]: true }));
        const cacheData = {
          ...vocSetting,
          isVoc: true,
        };

        setVocSettingsCache((prev) => ({
          ...prev,
          [selectedSurveyForVoc.id]: cacheData,
        }));
      }
    }
    setIsVocSettingModalVisible(false);
  };

  // VOC:VOC設定キャンセル関数
  const handleVocSettingCancel = () => {
    setIsVocSettingModalVisible(false);
    setSelectedSurveyForVoc(null);
    setEditingVocSettings(null);
  };

  const handlePublishSwitchChange = async (surveyId: string) => {
    const result = await props.changeSurveyPublish(surveyId);
    if (result) {
      setPublishMap((prev) => ({
        ...prev,
        [surveyId]: !prev[surveyId],
      }));
    }
  };

  const columns = () =>
    compact([
      {
        title: i18n.t("survey.table.title.publish"),
        dataIndex: "publish",
        key: "publish",
        render: (_: any, survey: Surveys): JSX.Element => {
          return (
            <>
              {publishMap[survey.id] && (
                <CheckOutlined className="survey-table-publish" />
              )}
            </>
          );
        },
      },
      {
        title: i18n.t("survey.table.title.meoSiteCount"),
        dataIndex: "site-count",
        key: "site-count",
        render: (_: any, survey: Surveys): JSX.Element => {
          return (
            <div>
              <span>{meoSiteCountMap.get(survey.id) || 0}</span>
            </div>
          );
        },
      },
      {
        title: i18n.t("survey.table.title.title"),
        dataIndex: "title",
        key: "title",
        sorter: (a: Surveys, b: Surveys) => {
          return (a.title || "").localeCompare(b.title || "");
        },
        render: (title: string, record: Surveys) => {
          return (
            <SMSLink
              to={`/${Page.survey}/edit`}
              queryParams={{
                id: record.id,
                type: "edit",
                mode: MODE_SURVEY,
                surveyType: parsed.surveyType || surveyTypeStore,
              }}
            >
              {title || i18n.t("survey.table.item.untitled")}
            </SMSLink>
          );
        },
      },
      {
        title: null,
        key: "buttons",
        render: (_: any, survey: Surveys): JSX.Element => {
          return (
            <div className="survey-table-buttons">
              <SMSLink
                to={`/${Page.survey}/settings`}
                queryParams={{
                  id: survey.id,
                  mode: MODE_SURVEY,
                  surveyType: parsed.surveyType || surveyTypeStore,
                }}
              >
                <Button icon={<SettingOutlined />}>
                  {i18n.t("survey.table.item.settings")}
                </Button>
              </SMSLink>
              <SMSLink
                to={`/${pageItem}`}
                queryParams={{
                  id: survey.id,
                  mode: MODE_SURVEY,
                  surveyType: parsed.surveyType || surveyTypeStore,
                }}
              >
                <Button icon={<ShopOutlined />}>
                  {i18n.t(`survey.table.item.${surveyLinkItem}`)}
                </Button>
              </SMSLink>
              <SMSLink
                to={`/${Page.survey}/reviewMediaSettings`}
                queryParams={{
                  id: survey.id,
                  mode: MODE_SURVEY,
                  surveyType: parsed.surveyType || surveyTypeStore,
                }}
              >
                <Button icon={<CommentOutlined />}>
                  {i18n.t("survey.table.item.reviewMediaSettings")}
                </Button>
              </SMSLink>
              {!isSurveyTypeStore && (
                <SMSLink
                  to={`/${Page.survey}/ecEmailSettings`}
                  queryParams={{
                    id: survey.id,
                    mode: MODE_SURVEY,
                    surveyType: parsed.surveyType || surveyTypeStore,
                  }}
                >
                  <Button icon={<FieldTimeOutlined />}>
                    {i18n.t("survey.table.item.ecEmailSettings")}
                  </Button>
                </SMSLink>
              )}
            </div>
          );
        },
      },
      {
        title: i18n.t("survey.table.title.surveyUrl"),
        dataIndex: "survey-url",
        key: "survey-url",
        render: (_: any, survey: Surveys): JSX.Element => {
          if (meoSiteCountMap.get(survey.id) === 0) {
            return (
              <div>
                <span>{i18n.t("survey.table.item.noSetMeoSite")}</span>
              </div>
            );
          }
          return (
            <div>
              <Dropdown
                overlay={() => urlQRCodeDropdown(survey.id, survey.title)}
                trigger={["click"]}
              >
                <Button type="link" icon={<CopyOutlined />} size="large" />
              </Dropdown>
            </div>
          );
        },
      },
      {
        title: i18n.t("survey.table.title.voc"),
        dataIndex: "voc-switch",
        key: "voc-switch",
        render: (_: any, survey: Surveys): JSX.Element => {
          const isVocActive = !!props.clientPlanDetails.voc;
          const content = (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Switch
                checked={isVocActive && (vocMap[survey.id] ?? false)}
                disabled={!isVocActive}
                onChange={() => {
                  handleVocSwitchChange(survey.id);
                }}
              />
              {isVocActive && vocMap[survey.id] && (
                <Button
                  type="text"
                  size="middle"
                  icon={<SettingOutlined />}
                  disabled={!isVocActive}
                  onClick={() => handleOpenVocSettingModal(survey.id)}
                  style={{ padding: "2px 4px" }}
                />
              )}
            </div>
          );
          return !isVocActive ? (
            <Tooltip title={i18n.t("survey.vocInactiveTooltip")}>
              <div style={{ display: "inline-block" }}>{content}</div>
            </Tooltip>
          ) : (
            content
          );
        },
      },
      {
        title: i18n.t("survey.table.title.publish"),
        dataIndex: "publish-switch",
        key: "publish-switch",
        render: (_: any, survey: Surveys): JSX.Element => {
          return (
            <div>
              <Switch
                checked={publishMap[survey.id]}
                onClick={() => {
                  handlePublishSwitchChange(survey.id);
                }}
              />
            </div>
          );
        },
      },
    ]);

  const addButton = (
    <SMSLink
      to={`${Page.survey}/add`}
      queryParams={{
        mode: MODE_SURVEY,
        surveyType: parsed.surveyType || surveyTypeStore,
      }}
    >
      <Button
        icon={<PlusOutlined />}
        type="primary"
        disabled={props.clientPlanDetails.plan === ""}
        style={{ marginRight: "14px" }}
      >
        {i18n.t("survey.table.settings.add")}
      </Button>
    </SMSLink>
  );

  const handleDuplicate = async () => {
    const selectedIds: Array<string> = props.selectedSurveys.map((s) => s.id!);
    const form: SurveysDeleteForm = {
      surveyIds: selectedIds,
    };
    await props.duplicateSurvey(form);
    await props.getSurveyList({ limit: 100 });
  };

  const duplicateButton = (
    <Button
      icon={<PlusOutlined />}
      type="primary"
      disabled={props.selectedSurveys.length < 1}
      onClick={handleDuplicate}
    >
      {i18n.t("survey.table.settings.duplicate")}
    </Button>
  );

  const handleDelete = () => {
    const selectedIds: Array<string> = props.selectedSurveys.map((s) => s.id!);
    const form: SurveysDeleteForm = {
      surveyIds: selectedIds,
    };
    props.deleteSurveys(form);
    setReviewLinkWarning();
  };

  const deleteButton = (
    <Popconfirm
      title={i18n.t("survey.table.settings.deleteCountConfirm", {
        count: props.selectedSurveys.length,
      })}
      disabled={props.selectedSurveys.length < 1}
      onConfirm={handleDelete}
      okText={i18n.t("common.delete")}
      cancelText={i18n.t("common.cancel")}
      okType="danger"
    >
      <Button
        danger={props.selectedSurveys.length > 0}
        disabled={props.selectedSurveys.length < 1}
        icon={<DeleteOutlined />}
        style={{ marginRight: "14px" }}
      >
        {i18n.t("common.delete")}
      </Button>
    </Popconfirm>
  );

  const handleDeleteAccessData = () => {
    const selectedIds: Array<string> = props.selectedSurveys.map((s) => s.id!);
    const form: SurveysDeleteForm = {
      surveyIds: selectedIds,
    };
    props.deleteAccessData(form);
  };

  const deleteAccessDataButton = (
    <Popconfirm
      title={i18n.t("survey.table.settings.deleteAccessDataConfirm", {
        count: props.selectedSurveys.length,
      })}
      disabled={props.selectedSurveys.length < 1}
      onConfirm={handleDeleteAccessData}
      okText={i18n.t("common.delete")}
      cancelText={i18n.t("common.cancel")}
      okType="danger"
    >
      <Button
        danger={props.selectedSurveys.length > 0}
        disabled={props.selectedSurveys.length < 1}
        icon={<DatabaseOutlined />}
        style={{ margin: 0 }}
      >
        {i18n.t("survey.table.settings.deleteAccessData")}
      </Button>
    </Popconfirm>
  );

  const selectedRowKeys = props.selectedSurveys.map(({ id }) => id);

  const handleSelect = (record: Surveys, isSelected: boolean) => {
    if (isSelected) {
      // Adding
      const newSelected = [
        ...props.selectedSurveys,
        {
          id: record.id,
        },
      ];
      props.setSelectedSurveys(newSelected);
    } else {
      // Removing
      let current = [...props.selectedSurveys];
      current = current.filter((c) => c.id !== record.id);
      props.setSelectedSurveys(current);
    }
  };

  const handleSelectAll = (isSelected: boolean, changeRows: Array<Surveys>) => {
    if (isSelected) {
      // Add all
      const newSelections = changeRows.map((a) => {
        return {
          id: a.id,
        };
      });

      // Add the newly selected rows, deduplicating by key
      const newResult = _.uniqBy(
        [...props.selectedSurveys, ...newSelections],
        (a: { id: string }) => {
          return a.id!;
        }
      );
      props.setSelectedSurveys(newResult);
    } else {
      // Remove all
      props.setSelectedSurveys([]);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onSelect: handleSelect,
    onSelectAll: handleSelectAll,
    preserveSelectedRowKeys: true,
  };

  const pagination = (
    <SimplePagination
      items={props.items.items}
      getCursor={(model: Surveys) => model.createdAt}
      sortDescending={true}
      hasRight={props.items.hasRight}
      hasLeft={props.items.hasLeft}
      location={props.location}
      history={history}
      match={props.match}
    />
  );
  return (
    <div>
      <div className="table-button-row">
        <Row className="form-button-row">
          {addButton}
          {duplicateButton}
          {deleteButton}
          {deleteAccessDataButton}
        </Row>
      </div>
      {props.clientPlanDetails.plan === "" && (
        <Alert
          message={
            <div>
              <p style={{ marginBottom: "0" }}>
                {i18n.t("errors.notSetKikikomiPlan")}
              </p>
            </div>
          }
          type="warning"
        />
      )}
      <Table
        rowSelection={rowSelection}
        dataSource={props.items.items}
        columns={columns()}
        rowKey={(surveys: Surveys) => surveys.id}
        size="middle"
        pagination={false}
        loading={props.listLoading}
      />
      <Row className="pagination-row">{pagination}</Row>
      {/* VOC設定モーダル */}
      <VocSettingModal
        key={selectedSurveyForVoc?.id}
        visible={isVocSettingModalVisible}
        survey={selectedSurveyForVoc}
        settings={editingVocSettings}
        onSave={handleVocSettingSave}
        onCancel={handleVocSettingCancel}
        onVocTypeChange={(value: number) => {
          setEditingVocSettings((prev) =>
            prev ? { ...prev, vocType: value } : prev
          );
        }}
        onVocTopPageNoteChange={(value: string) => {
          setEditingVocSettings((prev) =>
            prev ? { ...prev, vocTopPageNote: value } : prev
          );
        }}
        onVocCompletedNoteChange={(value: string) => {
          setEditingVocSettings((prev) =>
            prev ? { ...prev, vocCompletedNote: value } : prev
          );
        }}
      />
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    items: state.survey.items,
    listLoading: state.survey.listLoading,
    selectedSurveys: state.survey.selectedSurveys,
    multipleDeleteFinished: state.survey.multipleDeleteFinished,
    multipleDeleteLoading: state.survey.multipleDeleteLoading,
    clientPlanDetails: state.contractDetails.clientPlanDetails,
    meoSiteCountList: state.survey.meoSiteCountList,
    meoSiteCountListLoading: state.survey.meoSiteCountListLoading,
    deleteAccessDataLoading: state.survey.deleteAccessDataLoading,
    deleteAccessDataFinished: state.survey.deleteAccessDataFinished,
    error: state.survey.error,
  };
};

const mapDispatchToProps = {
  setPage,
  getSurveyList,
  getClientPlanDetails,
  deleteSurveys,
  clearItems,
  setSelectedSurveys,
  clearSelectedSurveys,
  clearSurvey,
  clearPickedQuestions,
  duplicateSurvey,
  changeSurveyVocSetting,
  changeSurveyPublish,
  getMeoSiteCountList,
  deleteAccessData,
};

export default connect(mapStateToProps, mapDispatchToProps)(SurveyTable);
